<template>

<div class="main list-view">

	<div class="sub-header">
		<div class="input-with-control">
			<input type="text" ref="search_term" v-model="filters.search_term" placeholder="Search..." @keyup.enter="getList(1)" v-focus>
			<div class="button action input-go" @click="getList(1)"><i class="fas fa-search"></i></div>
		</div>
		<div class="controls">
			<pagination :paging="paging" @updatePage="goToPage"></pagination>
		</div>
	</div>

	<div v-if="!orderId" class="filters">
		<div class="label-group">
			<label>Start</label>
			<input type="date" v-model="filters.start_date" :max="$today().clone().format('YYYY-MM-DD')">
		</div>
		<div class="label-group">
			<label>End</label>
			<input type="date" v-model="filters.end_date" :max="$today().clone().format('YYYY-MM-DD')">
		</div>
		
		<div class="label-group">
			<label></label>
			<ul class="checkbox-group">
				<li>
					<span :class="['pointer', 'far', filters.payment_date ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('payment_date')" title="Find Layaways with a payment within the date range."></span>
					<label class="pointer" title="Find Layaways with a payment within the date range.">Payment Date</label>
				</li>
				<li>
					<span :class="['pointer', 'far', filters.order_date ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('order_date')" title="Find Layaways with an original order date within the date range."></span>
					<label class="pointer" title="Find Layaways with an original order date within the date range.">Original Order Date</label>
				</li>
			</ul>
		</div>

		<div class="label-group">
			<label></label>
			<div class="button small neutral" @click="getList(1)">Filter Dates</div>
		</div>

		<div class="divider"></div>
		<label>Status...</label>
		<ul class="checkbox-group" style="margin: 0.5em 0.5em">
			<li>
				<span :class="['pointer', 'far', filters.open ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('open')"></span>
				<label>Open</label>
			</li>
			<li>
				<span :class="['pointer', 'far', filters.complete ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('complete')"></span>
				<label>Complete</label>
			</li>
			<li>
				<span :class="['pointer', 'far', filters.canceled ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('canceled')"></span>
				<label>Canceled</label>
			</li>
			<li>
				<span :class="['pointer', 'far', filters.all ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('all')"></span>
				<label>All</label>
			</li>
		</ul>
		<div class="divider"></div>

		<div class="extra-menu-item" @click="$router.push('/orders-sales/layaway-payments')">Layaway Payments Report</div>
		<div class="extra-menu-item" @click="showLayawaySettings = true">Layaway Settings</div>

		<div :class="['extra-menu-item', { 'disabled': gettingExport }]" @click="doExport()">Export CSV</div>
		<div class="message">{{ exportMessage }}</div>
		<div class="divider"></div>

		<div v-if="returnRoute" class="extra-menu-item" @click="returnToCustomer()"> Return to Customer </div>
		<router-link v-else :to="ordersRoute" class="extra-menu-item">Return to store orders</router-link>

	</div>

	<div v-if="orderId && !loading" class="filters">
		<button class="button neutral" @click="backToList()">Back To List</button>
		<div class="divider"></div>
        <div :class="['extra-menu-item', { disabled: !orderDetail.billing.email_address || loading },]"@click="sendOrderEmails()">
          {{ orderDetail.order.confirmation_sent ? "Res" : "S" }}end Emails
        </div>
        <div class="extra-menu-item" @click="printOrder('dealer')">Print Order</div>
        <div class="extra-menu-item" @click="printOrder('customer')">Print Customer Copy</div>
        <div class="extra-menu-item" @click="printPackingSlip()">Print Packing Slip</div>
        <div :class="['extra-menu-item', { disabled: isCancelDisabled() }]" @click="confirmCancelLayaway = true">
          Cancel Layaway
        </div>
        <div v-if="editAllowed">
            <div class="divider"></div>
            <div v-if="enableEdit" style="margin: 0 0 1em 0;">Click the <i class="fa fa-edit"></i> icon next to the section you want to edit</div>
            <div class="extra-menu-item" @click="enableEdit = !enableEdit">{{ enableEdit ? 'Cancel' : '' }} Edit Layaway</div>
            <div class="extra-menu-item" @click="getChangeLog()">{{ showChangeLog ? 'Hide' : 'Show' }} Change Log</div>
        </div>
	</div>

	<div v-if="!orderId" class="list">

		<div v-if="list.length == 0" class="empty-list">
			This list is empty
		</div>

		<table v-else class="data-list" cellpadding="0" cellspacing="0">
			<thead>
				<tr>
					<th class="list">Number</th>
					<th class="list">Date / Time</th>
					<th class="list">Customer</th>
					<th class="list">Status</th>
					<th class="list right">Order Total</th>
					<th class="list right">Last Payment</th>
					<th class="list right">Balance</th>
				</tr>
			</thead>
			<tr v-for="(order, index) in list" class="clickable-list-item" @click="getItem(order.order_id)">
				<td class="list">{{ order.order_number }}</td>
				<td class="list">{{ order.order_date }}<br />{{ order.order_time }}</td>
				<td class="list">
					{{ order.full_name }}
					<div v-if="order.address_1">
						{{ order.address_1 }} {{ order.city }} {{ order.state }}
						{{ order.zip_code }}
					</div>
				</td>
				<td class="list">{{ order.order_status }}</td>
				<td class="list right">{{ order.order_total }}</td>
				<td class="list right">{{ order.last_payment_date }}<br />{{ order.last_payment_amount }}</td>
				<td class="list right">{{ order.balance_remaining }}</td>
			</tr>
		</table>

	</div>

	<div v-if="!loading && orderId" class="detail">
		<order-detail 
			:orderDetail="orderDetail" 
			:orderStatusCodes="orderStatusCodes"
			:packingSlip="showPackingSlip"
            :showPrint="showPrint"
            :editLevel="orderDetail.order.order_method_code == 'LAYAWAY' ? 'partial' : 'all'"
			:enableEdit="enableEdit"
			:key="orderId + '-' + orderDetail.contact_id"
			@edit="setEditingSection($event)"
		/>
	</div>

	<div v-if="editingSection" class="modal">
		<div class="inner" style="max-width: 50em; max-height: 36em;">
			<h2>Edit Layaway</h2>
			<div class="content" style="max-height: 36em;">

				<div v-if="editingSection == 'customer'">
					<div>
						<h3>Customer Information</h3>
						<p>This is your customer's name and contact information. <b><span>Changes here will update the customer record and be reflected on all orders for this customer, not just this layaway.</span></b></p>
						<p><span class="inline-link" @click="setEditingSection('select_customer')"><span>Select a different customer instead of updating this one?</span></span></p>
						<div class="three-columns">
							<div>
								<label-required :condition="editingItem.first_name" label="First Name"/>
								<input type="text" v-model="editingItem.first_name" />
							</div>
							<div>
								<label>Middle Name</label>
								<input type="text" v-model="editingItem.middle_name" />
							</div>
							<div>
								<label-required :condition="editingItem.last_name" label="Last Name"/>
								<input type="text" v-model="editingItem.last_name" />
							</div>
						</div>
						<label-required :condition="editingItem.phone_number" label="Phone Number"/>
						<input type="text" v-model="editingItem.phone_number" />
						<label-required :condition="editingItem.email_address" label="Email Address"/>
						<input type="text" v-model="editingItem.email_address" />
					</div>
				</div>

				<div v-if="editingSection == 'billing'">
					<div>
						<h3>Billing Information</h3>
						<p>This is your customer's billing address. <b><span>Changes here will update the customer record and be reflected on all orders for this customer, not just this layaway.</span></b></p>
						<div class="three-columns">
							<div>
								<label-required :condition="editingItem.first_name" label="First Name"/>
								<input type="text" v-model="editingItem.first_name" />
							</div>
							<div>
								<label>Middle Name</label>
								<input type="text" v-model="editingItem.middle_name" />
							</div>
							<div>
								<label-required :condition="editingItem.last_name" label="Last Name"/>
								<input type="text" v-model="editingItem.last_name" />
							</div>
						</div>
						<label-required :condition="editingItem.address_1" label="Address"/>
						<input type="text" v-model="editingItem.address_1" />
						<label>Address 2</label>
						<input type="text" v-model="editingItem.address_2" />
						<div class="three-columns">
							<div>
								<label-required :condition="editingItem.city" label="City"/>
								<input type="text" v-model="editingItem.city" />
							</div>
							<div>
								<label-required :condition="editingItem.state" label="State"/>
								<input type="text" v-model="editingItem.state" />
							</div>
							<div>
								<label-required :condition="editingItem.zip_code" label="Zip Code"/>
								<input type="text" v-model="editingItem.zip_code" />
							</div>
						</div>
						<div class="two-columns">
							<div v-for="(customField, index) in editingItem.custom_fields">
								<label>{{ customField.label }}</label>
								<input type="text" v-model="editingItem[customField.custom_field_code]" />
							</div>
						</div>
					</div>
				</div>

				<div v-if="editingSection == 'select_customer'" class="two-one-columns">
					<div>
						<h3>Select a different customer</h3>
						<div class="input-with-control">
							<input type="text" ref="search_term" v-model="editSearchTerm" placeholder="Search..." @keyup.enter="searchCustomers()" v-focus>
							<div class="button action input-go" @click="searchCustomers()"><i class="fas fa-search"></i></div>
						</div>
						<div class="modal-search-result" style="max-height: none; height: 12em;">
							<p v-if="searchMessage" style="margin: 1em 0;" v-html="searchMessage"></p>
							<ul v-if="searchList.length > 0" class="search-list">
								<li class="clickable-list-item" v-for="(contact, index) in searchList" @click="assignContact(contact)">{{ contact.full_name }}</li>
							</ul>
						</div>
					</div>
					<div class="modal-instructions">
						<p>Current customer for this layaway is <b>{{ orderDetail.billing.first_name }} {{ orderDetail.billing.middle_name }} {{ orderDetail.billing.last_name }}</b>.</p>
						<p>Select a different customer to associate with this layaway.</p>
						<p>Changes here will update this layaway only.</p>
						<div v-if="showCreateCustomerOption">
							<div v-if="searchList.length > 0">Select a customer from the list.</div>
							<div>Search again if you don't see the customer you're looking for.</div>
							<div class="inline-link v-spaced" @click="setEditingSection('create_customer')">Or create a new customer.</div>
						</div>
					</div>
				</div>

				<div v-if="editingSection == 'create_customer'" class="two-one-columns">
					<div>
						<h3>Create a new customer</h3>
						<div class="three-columns">
							<div>
								<label-required :condition="newCustomer.first_name" label="First Name"/>
								<input type="text" v-model="newCustomer.first_name" />
							</div>
							<div>
								<label>Middle Name</label>
								<input type="text" v-model="newCustomer.middle_name" />
							</div>
							<div>
								<label-required :condition="newCustomer.last_name" label="Last Name"/>
								<input type="text" v-model="newCustomer.last_name" />
							</div>
						</div>
						<label-required :condition="newCustomer.address_1" label="Address"/>
						<input type="text" v-model="newCustomer.address_1" />
						<label>Address 2</label>
						<input type="text" v-model="newCustomer.address_2" />
						<div class="three-columns">
							<div>
								<label-required :condition="newCustomer.city" label="City"/>
								<input type="text" v-model="newCustomer.city" />
							</div>
							<div>
								<label-required :condition="newCustomer.state" label="State"/>
								<input type="text" v-model="newCustomer.state" />
							</div>
							<div>
								<label-required :condition="newCustomer.zip_code" label="Zip Code"/>
								<input type="text" v-model="newCustomer.zip_code" />
							</div>
						</div>
						<div class="two-columns">
							<div>
								<label-required :condition="newCustomer.phone_number" label="Phone Number"/>
								<input type="text" v-model="newCustomer.phone_number" />
							</div>
							<div>
								<label-required :condition="newCustomer.email_address" label="Email"/>
								<input type="text" v-model="newCustomer.email_address" />
							</div>
						</div>
					</div>
					<div class="modal-instructions">
						<p>Current customer for this layaway is <b>{{ orderDetail.billing.first_name }} {{ orderDetail.billing.middle_name }} {{ orderDetail.billing.last_name }}</b>.</p>
						<p>Create a new customer to associate with this layaway.</p>
						<p>Changes here will update this layaway only.</p>
					</div>
				</div>

				<div v-if="editingSection == 'shipping'" class="two-one-columns">
					<div>
						<div style="float: right; margin: 0;">
							<input type="radio" name="pickup" value="1" v-model="pickup" /> Pick up in-store<br>
							<input type="radio" name="pickup" value="0" v-model="pickup" /> Ship to customer
						</div>
						<h3>Shipping Information</h3>
						<div v-if="pickup == '1'" style="margin: 1em 0;">
							<p>This order is set to be picked up in-store.</p>
						</div>
						<div v-else style="margin: 1em 0;">
							<label-required :condition="editingItem.full_name" label="Full Name"/>
							<input type="text" v-model="editingItem.full_name" />
							<label-required :condition="editingItem.address_1" label="Address"/>
							<input type="text" v-model="editingItem.address_1" />
							<label>Address 2</label>
							<input type="text" v-model="editingItem.address_2" />
							<div class="three-columns">
								<div>
									<label-required :condition="editingItem.city" label="City"/>
									<input type="text" v-model="editingItem.city" />
								</div>
								<div>
									<label-required :condition="editingItem.state" label="State"/>
									<input type="text" v-model="editingItem.state" />
								</div>
								<div>
									<label-required :condition="editingItem.zip_code" label="Zip Code"/>
									<input type="text" v-model="editingItem.zip_code" />
								</div>
							</div>
							<div class="two-columns">
								<div>
									<label-required :condition="editingItem.shipping_charge" label="Shipping Charge"/>
									<input type="text" v-model="editingItem.shipping_charge" />
								</div>
								<div>
								</div>
							</div>
						</div>
					</div>
					<div class="modal-instructions">
						<p>Changing pickup / ship to will not affect other orders.</p>
						<div v-if="pickup != '1'">
							<p>This is your customer's shipping address.</p>
							<p>Changes here will update the customer's shipping address record and be reflected on all shipped orders for this customer, not just this layaway.</p>
						</div>
					</div>
				</div>

				<div v-if="editingSection == 'payments'" class="two-one-columns">
					<div>
						<h3>Payments</h3>
						<table class="data-list" cellpadding="0" cellspacing="0">
							<thead>
								<tr>
									<th class="list">Payment Date</th>
									<th class="list">Type</th>
									<th class="list right">Amount</th>
									<th class="list right"></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(payment, index) in orderDetail.payments">
									<td class="list">{{ payment.payment_date }}</td>
									<td class="list">{{ payment.description }}</td>
									<td class="list right">{{ payment.amount }}</td>
									<td class="list right"><div class="button icon" style="margin: 0 0.5em;"><i class="fa fa-trash" @click="confirmRemovePayment(payment)"></i></div></td>
								</tr>
							</tbody>
						</table>
						<p v-if="confirmMessage" style="margin: 1em 0;" v-html="confirmMessage"></p>
					</div>
					<div class="modal-instructions">
						<p>These are the payments associated with this layaway.</p>
						<p>Payments can <b>only</b> be refunded or adjusted through the POS system.</p>
						<p>Click <i class="fa fa-trash" style="font-size: 0.8em;"></i> to remove a payment if you believe it was recorded in error.</p>
					</div>
				</div>

				<div v-if="editingSection == 'items'" class="two-one-columns">
					<div>
						<h3 style="margin: 0 0 1em 0;">Items</h3>

						<table class="data-list" cellpadding="0" cellspacing="0" style="border-top: 1px dotted #ccc;">
							<tr v-for="(item, index) in orderDetail.items">
								<td class="list clickable-list-item" @click="setEditingSection('edit_item', item.order_item_id)">
									<div style="display: grid; grid-template-columns: 4em 1fr 6em 6em 4em; align-items: center; margin: 0.5em 0;">
										<img :src="item.image_src" style="max-width: 3em; max-height: 3em; margin: 0 0 0 0.5em;" />
										<div>
											<div><label>UPC</label> <b>{{ item.upc }}</b></div>
											<div class="truncate">{{ item.description }}</div>
											<div v-if="item.serial_number"><label>SN</label> {{ item.serial_number }}</div>
										</div>
										<div>
											<label>Cost</label><br><b>{{ item.order_cost }}</b>
										</div>
										<div>
											<label>Price</label><br><b>{{ item.order_price }}</b>
										</div>
										<div>
											<label>Qty</label><br><b>{{ item.quantity }}</b>
										</div>
									</div>
								</td>
							</tr>
						</table>
					</div>
					<div class="modal-instructions">
						<div v-if="orderDetail.order.order_method_code == 'LAYAWAY'">
							<p>This layaway was created while using the "Use date created" layaway setting.</p>
							<p><b>Only serial numbers can be edited.</b></p>
						</div>
						<p>Select an item to edit or remove from this layaway.</p>
						<p><span class="inline-link" @click="setEditingSection('add_item', editingItem.order_item_id)">Add an item?</span></p>
					</div>
				</div>

				<div v-if="editingSection == 'edit_item'" class="two-one-columns">
					<div>
						<h3 style="margin: 0 0 1em 0;">Edit Item in Layaway</h3>
						<div style="margin: 0 0 0.5em 0;">
							<label>UPC</label>
							<div><b>{{ editingItem.upc }}</b></div>
						</div>
						<div style="margin: 0 0 0.5em 0;">
							<label>Description</label>
							<div><b>{{ editingItem.description }}</b></div>
						</div>
						<div v-if="editingItem.previous_serial_number" style="margin: 0 0 0.5em 0;">
							<label-required :condition="editingItem.serial_number" label="Serial Number"/>
							<select v-model="editingItem.serial_number" @change="searchSerialNumber()">
								<option value="" disabled hidden>Select a serial number...</option>
								<option v-if="editingItem.previous_serial_number" :value="editingItem.previous_serial_number">{{ editingItem.previous_serial_number }}</option>
								<option v-for="serial in availableProductSerialNumbers" v-if="serial != editingItem.previous_serial_number" :value="serial">{{ serial }}</option>
							</select>
							<p v-if="searchMessage" v-html="searchMessage"></p>
						</div>
						<div class="three-columns">
							<div>
								<label-required :condition="editingItem.order_cost" label="Cost"/>
								<input type="text" v-model="editingItem.order_cost" :disabled="orderDetail.order.order_method_code == 'LAYAWAY'" />
							</div>
							<div>
								<label-required :condition="editingItem.order_price" label="Price"/>
								<input type="text" v-model="editingItem.order_price" :disabled="orderDetail.order.order_method_code == 'LAYAWAY'" />
							</div>
							<div>
								<label-required :condition="editingItem.quantity" label="Quantity"/>
								<input type="number" v-model="editingItem.quantity" :disabled="orderDetail.order.order_method_code == 'LAYAWAY' || editingItem.previous_serial_number || editingItem.order_distributor_id > 0" />
							</div>
						</div>
					</div>
					<div class="modal-instructions">
                        <div v-if="orderDetail.order.order_method_code == 'LAYAWAY'">
    						<p><b>This layaway was created while using the "Use date created" layaway setting. Only serial numbers can be edited.</b></p>
                        </div>
                        <div v-else>
	    					<p>Changes here will update the items on this layaway only.</p>
                        </div>
						<p v-if="editingItem.previous_serial_number"><b>This item has a serial number: quantity cannot be changed.</b></p>
						<p v-else-if="editingItem.order_distributor_id > 0"><b>This item has been fulfilled by a distributor: quantity cannot be changed.</b></p>
						<p v-if="editingItem.previous_serial_number && editingItem.order_distributor_id < 1">You can search for a different on-hand serial number to verify it exists and then assign it to this item.</p>
						<p><span class="inline-link" style="color: red" @click="setEditingSection('remove_item', editingItem.order_item_id)">Remove this item?</span></p>
					</div>
				</div>

				<div v-if="editingSection == 'remove_item'">
					<div>
						<h3 style="margin: 0 0 1em 0;">Remove Item from Layaway</h3>
						<div v-if="Object.values(orderDetail.items).length == 1">
							<p>This is the only item in this layaway. It cannot be removed.</p>
							<p>If you would like to replace this item with another, add the new item first, and then return here to remove this item.</p>
						</div>
						<div v-else>
							<p>You are removing the item <b>{{ selelectedDescription }}</b> from this layaway.</p>
							<p>Changes here will update the items on this layaway only.</p>
							<p>Removed items will be returned to stock.</p>
							<p><b>Editing this order could change your sales tax liability. If you've already remitted sales tax for this layaway, you'll need to handle any over- or under-payments through your accounting system.</b></p>
							<p>Click <b>Save</b> to remove the item from this layaway.</p>
						</div>
					</div>
				</div>

				<div v-if="editingSection == 'add_item'" class="two-one-columns">
					<div>
						<h3 style="margin: 0 0 1em 0;">Add an Item by UPC</h3>
						<div class="input-with-control">
							<input type="text" ref="search_term" v-model="editSearchTerm" placeholder="Search..." @keyup.enter="searchProducts()" v-focus>
							<div class="button action input-go" @click="searchProducts()"><i class="fas fa-search"></i></div>
						</div>
						<div v-if="selectedId">
							<div style="margin: 0 0 0.5em 0;">
								<label>UPC</label>
								<div><b>{{ editingItem.upc }}</b></div>
							</div>
							<div style="margin: 0 0 0.5em 0;">
								<label>Description</label>
								<div><b>{{ editingItem.description }}</b></div>
							</div>
							<div v-if="editingItem.serializable" style="margin: 0 0 0.5em 0;">
								<label>Serial Number</label>
								<div class="input-with-control">
									<select v-model="editingItem.serial_number" @change="searchSerialNumber()">
										<option value="" disabled hidden>Select a serial number...</option>
										<option v-if="editingItem.previous_serial_number" :value="editingItem.previous_serial_number">{{ editingItem.previous_serial_number }}</option>
										<option v-for="serial in availableProductSerialNumbers" v-if="serial != editingItem.previous_serial_number" :value="serial">{{ serial }}</option>
									</select>
								</div>
								<p v-if="searchMessage" v-html="searchMessage"></p>
							</div>
							<div class="three-columns">
								<div>
									<label>Cost</label>
									<input type="text" v-model="editingItem.cost" />
								</div>
								<div>
									<label>Price</label>
									<input type="text" v-model="editingItem.price" />
								</div>
								<div>
									<label>Quantity</label>
									<input type="number" v-model="editingItem.quantity" min="1" :max="editingItem.available" :disabled="editingItem.serializable > 0" />
								</div>
							</div>
						</div>
						<div v-else class="modal-search-result" style="max-height: none; height: 12em;">
							<p v-if="searchMessage" style="margin: 1em 0;" v-html="searchMessage"></p>
							<div v-if="searchList.length > 1" class="search-list">
								<div class="clickable-list-item three-columns" style="grid-template-columns: 5em 1fr 5em; margin: 0 0 0.5em 0;" v-for="(product, index) in searchList" @click="assignProduct(product)">
									<img :src="product.image_src" style="max-width: 4em; max-height: 4em;" />
									<div><label>UPC</label> <b>{{ product.upc }}</b><br>{{ product.short_description }}</div>
									<div class="right">${{ product.price }}</div>
								</div>
							</div>
						</div>
					</div>
					<div class="modal-instructions">
						<p>You are selecting an item to add to this layaway.</p>
						<p>Changes here will update the items on this layaway only.</p>
					</div>
				</div>

				<div v-if="editingSection == 'extra'" class="two-one-columns">
					<div>
						<h3>Shipping, Handling, Tax</h3>
						<div class="three-columns">
							<div>
								<label>Shipping</label>
								<input type="text" v-model="editingItem.shipping_charge" />
							</div>
							<div>
								<label>Handling</label>
								<input type="text" v-model="editingItem.handling_charge" />
							</div>
							<div>
								<label>Sales Tax</label>
								<input type="text" v-model="editingItem.tax_charge" />
							</div>
						</div>

					</div>
					<div class="modal-instructions">
						<p>These are the additional charges associated with this layaway.</p>
						<p>Changes here will update this layaway only.</p>
					</div>
				</div>

				<div v-if="editingSection == 'discounts'" class="two-one-columns">
					<div>
						<h3>Discounts</h3>
						<table class="data-list" cellpadding="0" cellspacing="0">
							<thead>
								<tr>
									<th class="list">Reason (required)</th>
									<th class="list">Note</th>
									<th class="list">Amount</th>
									<th class="list"></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(discount, index) in editingItem">
									<td class="list" style="padding: 0.25em 0.25em 0 0;">
										<select v-model="discount.reason_code_id">
											<option value="">Reason...</option>
											<option v-for="reason in reasonCodes" :value="reason.reason_code_id">{{ reason.description }}</option>
										</select>
									</td>
									<td class="list" style="padding: 0.25em 0.25em 0 0;"><input type="text" v-model="discount.notes" /></td>
									<td class="list" style="padding: 0.25em 0.25em 0 0;"><input type="text" v-model="discount.amount" style="width: 5em;"/></td>
									<td class="list"><div class="button icon"><i class="fa fa-trash" @click="removeOrderDiscount(index)"></i></div></td>
								</tr>
							</tbody>
						</table>
					</div>
					<div class="modal-instructions">
						<p>These are the discounts associated with this layaway.</p>
						<p>Changes here will update this layaway only.</p>
						<p><span class="inline-link" @click="addOrderDiscount()"><span>Add an order discount</span></span></p>
					</div>
				</div>

				<div v-if="editingSection == 'fees'" class="two-one-columns">
					<div>
						<h3>Order Fees</h3>
						<table class="data-list" cellpadding="0" cellspacing="0">
							<thead>
								<tr>
									<th class="list">Description</th>
									<th class="list" style="padding: 0 0 0 1em;">Amount</th>
									<th class="list"></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(fee, index) in editingItem.fees">
									<td class="list"><input type="text" v-model="fee.description" style="width: 18em; margin: 0.25em 0;" /></td>
									<td class="list"><input type="text" v-model="fee.amount" style="width: 5em; margin: 0.25em 1em;" /></td>
									<td class="list"><div class="button icon" style="margin: 0 0.5em;" @click="removeOrderFee(index)"><i class="fa fa-trash"></i></div></td>
								</tr>
							</tbody>
						</table>
						<div v-if="editingItem.item_fees.length > 0" style="margin: 1em 0 0 0;">
							<h3 >Item Fees</h3>
							<table class="data-list" cellpadding="0" cellspacing="0">
								<thead>
									<tr>
										<th class="list">Description</th>
										<th class="list" style="padding: 0 0 0 1em;">Amount</th>
										<th class="list"></th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="(item_fee, index) in editingItem.item_fees">
										<td class="list"><input type="text" v-model="item_fee.description" style="width: 18em; margin: 0.25em 0;" /></td>
										<td class="list"><input type="text" v-model="item_fee.amount" style="width: 5em; margin: 0.25em 1em;" /></td>
										<td class="list"><div class="button icon" style="margin: 0 0.5em;" @click="removeOrderItemFee(index)"><i class="fa fa-trash"></i></div></td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
					<div class="modal-instructions">
						<p>These are the fees associated with this layaway.</p>
						<p>Changes here will update this layaway only.</p>
						<p><span class="inline-link" @click="addOrderFee()"><span>Add an order fee</span></span></p>
					</div>
				</div>

			</div>

			<div class="controls">
				<div class="button cancel spaced" @click="cancelEditModal()">{{ isTopLevelMenu ? 'Cancel' : 'Back' }}</div>
				<div v-if="!hideSaveButton" :class="['button', editingSection.indexOf('select_') == -1 || this.selectedId ? 'action' : 'neutral', 'spaced']" @click="saveSection(editingSection)">Save</div>
				<div v-else class="button spaced disabled">Save</div>
			</div>
		</div>
	</div>

    <div class="modal" v-if="confirmCancelLayaway">
      <div class="inner">
        <h2>{{ orderDetail.totals.payment_total > 0 ? 'Cannot' : 'Confirm' }} Cancel Layaway</h2>
        <div class="content">
			<div v-if="orderDetail.order.order_status_code == 'COMPLETE'" class="center">
				<i class="fa fa-exclamation-triangle icon-warning" style="font-size: 2em; margin: 0 0 0.5em 0;"></i>
				<p><b>This layaway has already been completed!</b></p>
				<p>You can return items and refund payments through POS.</p>
			</div>
			<div v-else-if="orderDetail.order.order_status_code == 'CANCELED'" class="center">
				<i class="fa fa-exclamation-triangle icon-warning" style="font-size: 2em; margin: 0 0 0.5em 0;"></i>
				<p><b>This layaway has already been canceled!</b></p>
				<p>You can manually change the order status if you believe this has been marked as canceled in error.</p>
			</div>
			<div v-else-if="orderDetail.totals.payment_total > 0" class="center">
				<i class="fa fa-exclamation-triangle icon-warning" style="font-size: 2em; margin: 0 0 0.5em 0;"></i>
				<p><b>This layaway has associated payments!</b></p>
				<p>Use POS to refund layaway payments back to your customer, then return here to cancel and return any remaining items to stock.</p>
			</div>
			<div v-else>
				<p v-if="Object.keys(orderDetail.items).length > 0">Canceling this layaway will return all items to inventory.</p>
				<p v-else>This layaway does not have any items, it will be marked as canceled.</p>
				<p style="text-align: center;">Do you want to proceed?</p>
			</div>
          <div v-if="confirmMessage" class="empty">{{ confirmMessage }}</div>
        </div>
        <div class="controls">
          <div class="button cancel spaced" @click="confirmCancelLayaway = false">
            {{ orderDetail.totals.payment_total > 0 ? 'Got It' : 'No' }}
          </div>
          <div v-if="orderDetail.totals.payment_total <= 0 && !isCancelDisabled()" class="button action spaced" @click="cancelLayaway()">
            Yes, Cancel Layaway
          </div>
        </div>
      </div>
    </div>

	<div v-if="showLayawaySettings" class="modal">
		<div class="inner">
			<h2>Layaway Settings</h2>
			<div class="content">
				<p>When should layaways show on sales and tax reports?</p>
				<div class="two-columns">
					<div>
						<h3>Date created</h3>
						<p>Layaway items, fees, discounts and sales tax will be reported on the date the layaway is created.</p>
						<p><b>With this option, only customer info and product serial numbers are editable.</b></p>
						<div style="font-size: 1.15em">
							<input type="radio" id="created" value="created" v-model="layawayReportingBasis">
							<label for="created"> Use date created</label>
						</div>
					</div>
					<div>
						<h3>Date paid in full</h3>
						<p>Layaway items, fees, discounts and sales tax will be reported on the date the layaway is paid in full.</p>
						<p><b>With this option, customer info and all products are editable until the layaway is completed.</b></p>
						<div style="font-size: 1.15em">
							<input type="radio" id="paid_in_full" value="paid_in_full" v-model="layawayReportingBasis">
							<label for="paid_in_full"> Use date paid in full</label>
						</div>
					</div>
				</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showLayawaySettings = false">Cancel</div>
				<div :class="['button', 'action', 'spaced']" @click="saveLayawaySettings()">Save</div>
			</div>
		</div>
	</div>

	<div v-if="showChangeLog" class="modal medium-modal">
		<div class="inner" style="width: 60em;">
			<h2>Change Log</h2>
			<div class="content">
				<p v-if="searchMessage">{{ searchMessage }}</p>
				<table v-if="changeLog.length > 0" class="change-log list" style="width: 100%;">
					<thead>
						<tr>
							<th class="list-item">Date / Time</th>
							<th class="list-item">User</th>
							<th class="list-item">From</th>
							<th class="list-item">To</th>
							<th class="list-item"></th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(change, index) in changeLog">
							<td class="list-item">{{ $moment.utc(change.log_time).local().format('MM/DD/YYYY') }}<br />{{ $moment.utc(change.log_time).local().format('hh:mm A') }}</td>
							<td class="list-item">{{ change.user_name }}</td>
							<td class="list-item">
								<div v-for="(line, index) in change.from_data">
									{{ line.field }}: {{ line.value }}
								</div>
							</td>
							<td class="list-item">
								<div v-for="(line, index) in change.to_data">
									{{ line.field }}: {{ line.value }}
								</div>
							</td>
							<td class="list-item"></td>
						</tr>
					</tbody>
				</table>
				<div v-else class="empty">No changes found.</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showChangeLog = false">Cancel</div>
			</div>
		</div>
	</div>

	<message-modal v-if="message" :message="message" :loading="loading || saving" :errorStatus="messageStatus" :timeout="messageTimeout" @dismiss="message = ''" />

	<iframe id="download" src="" style="display: none;"></iframe>

</div>

</template>

<script>
import OrderDetail from '@/components/OrderDetail.vue'
import LabelRequired from '@/components/LabelRequired'
export default {
	name: 'report-list',
	props: ['order_id'],
	components: {
		'order-detail': OrderDetail,
		'label-required': LabelRequired
	},
	data() {
		return {
			loading: false,
			searching: false,
			saving: false,
			message: '',
			messageStatus: '',
			messageTimeout: 0,
			page: 1,
			filters: {},
			default_start_date: '',
			default_end_date: '',
			list: [],
			paging: {},
			orderStatusCodes: [],
			showLayawaySettings: false,
			showChangeLog: false,
			orderDetail: {},
			pickup: '0', // root level var allows us to toggle pickup/ship to without affecting the orderDetail.shipping.pickup value until we save the change
			exportMessage: '',
			gettingExport: false,
			orderId: null,
            editAllowed: false,
			enableEdit: false,
			editingSection: '',
			editSearchTerm: '',
			searchList: [],
			searchMessage: '',
			selectedId: null,
			selelectedDescription: '',
			editingItem: {},
			changeLog: [],
			showCreateCustomerOption: false,
			newCustomer: {},
			hideSaveButton: false,
			confirmMessage: '',
			layawayReportingBasis: '',
			paymentToRemove: null,
			showPackingSlip: false,
            showPrint: false,
			confirmCancelLayaway: false,
			reasonCodes: [],
			ordersRoute: '/orders-sales/orders/store',
			serialChecked: false, 
			returnRoute: '',
			availableProductSerialNumbers: []
		}
	},
	created() {
		this.returnRoute = sessionStorage.getItem('returnRoute') || ''
		this.getOrderMethods()
		this.clearFilters(true)
	},
	computed: {
		isTopLevelMenu () {
			const topLevelMenus = 
				new Set(
					['items', 'customer', 'fees', 'payments', 'discounts', 'billing', 'extra']
				)
			return topLevelMenus.has(this.editingSection)
		},
		getLayawayProductIds() {
			return Object.values(this.orderDetail.items).map(item => item.product_id)
		}
	},
	methods: {
		getOrderMethods() {
			this.apiCall({ destination: "get_list_order_control_lists" }).then((obj) => {
				this.orderStatusCodes = obj.order_status;
				this.layawayReportingBasis = obj.layaway_reporting_basis || 'created'
			})
			.catch((err) => {
				console.log(err);
			});
		},
		clearFilters(getList) {
			this.filters = {
				order_method_code: 'LAYAWAY',
				contact_id: this.$route.query.contact_id || '', 
				open: true,
				closed: false,
				canceled: false,
				page: 1,
				search_term: '',
				start_date: '',
				payment_date: true,
				order_date: false,
				end_date: '',
				order_id: '',
				do_export: false
			}
			var oneMonthAgo = new Date()
			oneMonthAgo.setMonth(oneMonthAgo.getMonth() -1)
			this.default_start_date = oneMonthAgo.toISOString().slice(0,10)
			this.default_end_date = new Date().toISOString().slice(0,10)
            // if there are filters is session storgage?
            var sessionFilters = sessionStorage.getItem("order_list_filters");
            if (sessionFilters) {
                this.default_start_date = JSON.parse(sessionFilters).start_date || this.default_start_date;
                this.default_end_date = JSON.parse(sessionFilters).end_date || this.default_end_date;
            }

			if (this.filters.contact_id) {
				this.filters.start_date = ''
				this.filters.end_date = ''
				this.filters.payment_date = false
				this.filters.order_date = false
				this.filters.open = false
				this.filters.all = false
			} else {
            	this.filters.start_date = this.default_start_date
				this.filters.end_date = this.default_end_date
			}

			if (getList) {
				this.getList(1)
			}
		},
		toggleFilter(which) {
			// this.filters[which] = !this.filters[which]
            var exclusiveFilters = ['open', 'complete', 'canceled', 'all']
            if (exclusiveFilters.includes(which)) {
                this.filters[which] = !this.filters[which]
                if (this.filters[which]) {
                    exclusiveFilters.forEach(filter => {
                        if (filter != which) {
                            this.filters[filter] = false
                        }
                    })
                }
            } else {
                this.filters.payment_date = !this.filters.payment_date
                this.filters.order_date = !this.filters.order_date
            }
			this.filters.page = 1
			this.getList()
		},
		getList(page) {
			if (this.loading) { return }
			if (page) {
				this.filters.page = page
			}
			this.loading = true
			this.message = 'Loading layaways...'
			this.messageStatus = 'waiting'
			this.apiCall({ destination: 'get_list_orders_layaway', data: this.filters }).then(obj => {
				this.loading = false
				this.messageStatus = obj.status
				if (this.filters.do_export) {
					this.filters.do_export = 0
					document.getElementById("download").src = obj.download_url
					this.gettingExport = false
					this.exportMessage = ''
					this.message = ''
				} else {
					this.message = ''
					this.list = obj.list
					this.paging = obj.paging
				}
			}).catch(err => {
				console.log(err)
			})
		},
		goToPage(page) {
			this.filters.page = page
			this.getList()
		},
		saveLayawaySettings() {
			if (this.saving) { return }
			this.saving = true
			this.message = 'Saving layaway settings...'
			this.messageStatus = 'waiting'
			this.apiCall({ destination: 'save_item_preference', data: { preference_code: 'LAYAWAY_REPORTING_BASIS', value: this.layawayReportingBasis } }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					this.showLayawaySettings = false
					this.message = 'Layaway settings saved.'
					this.messageTimeout = 2000
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		getItem(orderId) {
			this.loading = true
			this.message = 'Loading layaway...'
			this.messageStatus = 'waiting'
			this.orderDetail = {}
			this.apiCall({ destination: 'get_item_order', data: { order_id: orderId } }).then(obj => {
				this.orderId = orderId
				this.orderDetail.order = obj.order_detail
				this.orderDetail.billing = obj.order_billing_address
				this.orderDetail.custom_fields = obj.order_billing_custom_fields
				// convert custom_fields array to object on orderDetail.billing for saving
				if (obj.order_billing_custom_fields) {
					obj.order_billing_custom_fields.forEach(key => {
						this.orderDetail.billing[key.custom_field_code] = key.value
					})
				}
				this.orderDetail.shipping = obj.order_shipping_address
				this.pickup = obj.order_shipping_address.pickup
				this.orderDetail.items = obj.order_items
		        this.orderDetail.item_fees = obj.order_item_fees;
				this.orderDetail.discounts = obj.order_discounts
				this.orderDetail.fees = obj.order_fees
				this.orderDetail.payments = obj.order_payments
				this.orderDetail.totals = obj.order_totals
				this.orderStatus = obj.order_detail.order_status_code
				this.loading = false
				this.message = ''
				this.messageStatus = ''
                this.editAllowed = obj.edit_allowed
			}).catch(err => {
				console.log(err)
			})
		},
		setEditingSection(section, selectedId = null) {
			this.editingSection = section
			this.editSearchTerm = ''
			this.searchList = []
			this.selectedId = selectedId
			this.showCreateCustomerOption = false
			this.newCustomer = {}
			this.editingItem = {}
			this.confirmMessage = ''
			switch (section) {
				case 'customer':
				case 'billing':
					this.editingItem = JSON.parse(JSON.stringify(this.orderDetail.billing))
					this.editingItem.custom_fields = JSON.parse(JSON.stringify(this.orderDetail.custom_fields))
					this.hideSaveButton = false
					break;
				case 'select_customer':
					this.hideSaveButton = true
					break;
				case 'create_customer':
					this.hideSaveButton = false
					break;
				case 'shipping':
					if (this.orderDetail.shipping.pickup == '1') {
						// empty shipping so populate with billing info
						this.editingItem = JSON.parse(JSON.stringify(this.orderDetail.billing))
						this.editingItem.full_name = this.editingItem.first_name + ' ' + this.editingItem.middle_name + ' ' + this.editingItem.last_name
					} else {
						this.editingItem = JSON.parse(JSON.stringify(this.orderDetail.shipping))
					}
					if (this.orderDetail.totals.shipping_charge > 0) {
						this.editingItem.shipping_charge = this.orderDetail.totals.shipping_charge
					}
					this.hideSaveButton = false
					break;
				case 'payments':
					this.hideSaveButton = true
					break;
				case 'items':
					this.hideSaveButton = true
					break;
				case 'edit_item':
				case 'remove_item':
					this.hideSaveButton = false
					// iterate orderDetail.items and set the selelectedDescription to the description of the item with a order_item_id matching this.selectedId
					Object.values(this.orderDetail.items).forEach(item => {
						if (item.order_item_id == selectedId) {
							// deep copy item to editingItem
							this.editingItem = JSON.parse(JSON.stringify(item))
							this.editingItem.previous_quantity = item.quantity
							if (item.serial_number) {
								this.editingItem.previous_serial_number = item.serial_number
								this.getAvailableProductSerials(item.product_id)
							}
							this.selelectedDescription = item.upc + ' ' + item.description
						}
					})
					break;
				case 'extra':
					this.hideSaveButton = false
					this.editingItem = JSON.parse(JSON.stringify(this.orderDetail.totals))
					break;
				case 'discounts':
					if (this.reasonCodes.length == 0) {
						this.apiCall({ destination: 'get_list_reason_codes' }).then(obj => {
							if (obj.status == 'success') {
								this.reasonCodes = obj.reason_codes
							}
						}).catch(err => {
							console.log(err)
						})
					}
					this.hideSaveButton = false
					this.editingItem = JSON.parse(JSON.stringify(this.orderDetail.discounts))
					break;
				case 'fees':
					this.hideSaveButton = false
					this.editingItem = {
						fees: JSON.parse(JSON.stringify(this.orderDetail.fees)),
						item_fees: JSON.parse(JSON.stringify(this.orderDetail.item_fees))
					}
					break;
			}
		},
		searchCustomers() {
			// search for customers matching this.customerSearchTerm and display results in the select customer modal
			if (this.searching || !this.editSearchTerm) { return }
			this.searching = true
			this.searchList = []
			this.selectedId = null
			this.showCreateCustomerOption = false
			this.searchMessage = 'Searching customers...'
			this.apiCall({ destination: 'get_list_contacts', data: { search_term: this.editSearchTerm } }).then(obj => {
				this.searching = false
				if (obj.status == 'success') {
					// display search results in the select customer modal
					if (obj.list.total_items > 0) {
						this.searchMessage = ''
						this.searchList = obj.list.customers
					} else {
						this.searchMessage = 'No customers found matching "' + this.editSearchTerm + '"'
					}
					this.showCreateCustomerOption = true
				}
			}).catch(err => {
				console.log(err)
			})
		},
		assignContact(contact) {
			// assign this contact to the orderDetail.billing object and save
			this.searchList = []
			this.searchMessage = 'Click save to assign <b>' + contact.full_name + '</b> to this layaway.'
			this.selectedId = contact.contact_id
			this.hideSaveButton = false
		},
		searchProducts() {
			if (this.searching || !this.editSearchTerm) { return }
			this.searching = true
			this.searchList = []
			this.searchMessage = 'Searching products...'
			this.apiCall({ destination: 'get_item_product_by_upc', data: { upc: this.editSearchTerm, get_price: true } }).then(obj => {
				this.searching = false
				if (obj.status == 'success') {
					if (obj.product_list.length == 1) {
						this.assignProduct(obj.product_list[0])
					} else if (obj.product_list.length > 0) {
						this.searchMessage = ''
						this.searchList = obj.product_list
					} else {
						this.searchMessage = 'No products found matching "' + this.editSearchTerm + '"'
					}
				} else if (obj.status == 'error') {
					this.searchMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		assignProduct(product) {
			this.searchList = []
			this.searchMessage = ''
			this.selectedId = product.product_id
			this.editingItem = {
				product_id: product.product_id,
				upc: product.upc,
				description: product.short_description,
				cost: product.cost,
				price: product.price,
				serializable: product.serializable,
				serial_number: '',
				available: product.available,
				distributor_id: product.distributor_id || 0,
				quantity: 1
			}
			if (product.serializable) {
				this.getAvailableProductSerials(product.product_id)
			}
			this.hideSaveButton = false
		},
		confirmRemovePayment(payment) {
			this.paymentToRemove = payment
			this.selectedId = payment.order_payment_id
			this.confirmMessage = 'Are you sure you want to remove the payment of <b>' + payment.amount + '</b> made on <b>' + payment.payment_date + '</b>?<br><br>Click Save to confirm.'
			this.hideSaveButton = false
		},
		getAvailableProductSerials(productId) {
			if (this.searching) { return }
			this.availableProductSerialNumbers = []
			this.searching = true
			this.searchMessage = ''
			this.apiCall({ destination: 'get_available_product_serial_numbers', data: { product_id: productId } }).then(obj => {
				this.searching = false
				if (obj.status == 'success') {
					if (obj.serial_numbers) {
						this.availableProductSerialNumbers = obj.serial_numbers
					} else {
						this.searchMessage = 'No available serial numbers found.'
					}
				}
			}).catch(err => {
				console.log(err)
			})
		},
		searchSerialNumber() {
			if (this.editingItem.serial_number == 'Select a serial number...' || this.searching || !this.editingItem.serial_number || this.editingItem.serial_number == this.editingItem.previous_serial_number) { return }
			this.searching = true
			this.searchMessage = 'Searching for serial number...'
			this.apiCall({ destination: 'get_item_product_serial_number', data: { product_id: this.editingItem.product_id, serial_number: this.editingItem.serial_number } }).then(obj => {
				this.searching = false
				if (obj.status == 'success') {
					if (obj.serial_number) {
						this.searchMessage = 'Serial number ' + this.editingItem.serial_number + ' can be assigned to this item.'
						this.serialChecked = true
					} else {
						this.searchMessage = 'Available serial number ' + this.editingItem.serial_number + ' not found.'
						this.editingItem.serial_number = this.editingItem.previous_serial_number ? this.editingItem.previous_serial_number : ''
						this.serialChecked = false
					}
				}
			}).catch(err => {
				console.log(err)
			})
		},
		saveSection(section) {
			// save the changes made in the section specified by the section param
			switch (section) {
				case 'customer':
				case 'billing':
					if (!this.editingItem.first_name || !this.editingItem.last_name || !this.editingItem.phone_number || !this.editingItem.email_address) {
						this.message = 'First name, last name, phone number, and email address are required.'
						this.messageStatus = 'invalids'
						return
					}
					this.saveCustomer(section)
					break;
				case 'select_customer':
					// assign the selected contact to this order and save
					if (this.selectedId) {
						this.saveLayawayCustomer()
					}
					break;
				case 'create_customer':
					if (!this.newCustomer.first_name || !this.newCustomer.last_name || !this.newCustomer.phone_number || !this.newCustomer.email_address) {
						this.message = 'First name, last name, phone number, and email address are required.'
						this.messageStatus = 'invalid'
						return
					}
					// assign the selected contact to this order and save
					this.saveNewCustomer()
					break;
				case 'shipping':
					// save changes to shipping address and pickup/ship to
					this.saveShipping()
					break;
				case 'payments':
					// save changes to payments
					if (this.selectedId) {
						this.removePayment()
					}
					break;
				case 'edit_item':
					if (!this.editingItem.order_cost || !this.editingItem.order_price || !this.editingItem.quantity) {
						this.message = 'Cost, price, and quantity are required.'
						this.messageStatus = 'invalid'
						return
					}
					if (this.editingItem.serializable == "1" && this.editingItem.serial_number != this.editingItem.previous_serial_number && !this.serialChecked) {
						this.message = 'Serial number cannot be changed without searching to verify.'
						this.messageStatus = 'invalid'
						return
					}
					// save changes to an item on this layaway
					if (this.selectedId) {
						this.saveOrderItem()
					}
					break;
				case 'add_item':
					if (!this.editingItem.cost || !this.editingItem.price || !this.editingItem.quantity) {
						this.message = 'Cost, price, and quantity are required.'
						this.messageStatus = 'invalid'
						return
					}
					if (this.editingItem.serializable == "0" && this.isProductInLayaway(this.editingItem.product_id)) {
						this.message = 'Product already exists in layaway. Adjust quantity instead.'
						this.messageStatus = 'invalid'
						this.setEditingSection('items')
						return
					}
					// add an item to this layaway
					if (this.selectedId) {
						this.addOrderItem()
					}
					break;
				case 'remove_item':
					// remove an item to this layaway
					if (Object.values(this.orderDetail.items).length == 1) {
						this.message = 'You cannot remove the only item from a layaway.'
						this.messageStatus = 'invalid'
						break;
					}
					if (this.selectedId) {
						this.removeOrderItem()
					}
					break;
				case 'extra':
					// save changes to extra charges
					this.saveExtraCharges()
					break;
				case 'fees':
					// save changes to fees
					this.saveFees()
					break;
				case 'discounts':
					// save changes to discounts
					this.saveDiscounts()
					break;
			}
		},
		saveCustomer(section) {
			// iterate this.editingItem to see if any key has changed from the original orderDetail.billing value for that key. If any changes are found, save
			let changeLog = []
			let changesFound = false
			for (let key in this.editingItem) {
				if (this.editingItem[key] !== this.orderDetail.billing[key]) {
					changeLog.push({ field: key, old_value: this.orderDetail.billing[key], new_value: this.editingItem[key] })
					changesFound = true
					// break
				}
			}
			if (!changesFound) {
				return
			}
			// save changes to this order's customer record
			if (this.saving) { return }
			this.saving = true
			this.message = 'Saving customer...'
			this.messageStatus = 'waiting'
			this.apiCall({ destination: 'save_item_contact', data: this.editingItem }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					// any serialized items that might need updating in log books?
					let serialNumbers = []
					// iterate this.orderDetail.items and if any item has a serial number, add an entry to the change log for that item with the old serial number and the new serial number
					Object.values(this.orderDetail.items).forEach(item => {
						if (item.serial_number) {
							serialNumbers.push({ serial_number: item.serial_number, product_id: item.product_id })
						}
					})
					if (serialNumbers.length > 0) {
						this.apiCall({ destination: 'save_disposed_to_contact_info', data: { contact_id: this.editingItem.contact_id, serial_numbers: serialNumbers } }).then(obj => {
							if (obj.status != 'success') {
								this.message = obj.message
							}
						}).catch(err => {
							console.log(err)
						})
					}
					this.saveChangeLog(changeLog)
					this.editingSection = ''
					this.editingItem = {}
					this.enableEdit = false
					this.message = 'Customer saved!'
					this.messageTimeout = 2000
					this.getItem(this.orderId)
				}
			}).catch(err => {
				console.log(err)
			})
		},
		saveNewCustomer() {
			if (this.saving) { return }
			this.saving = true
			this.message = 'Saving new customer...'
			this.messageStatus = 'waiting'
			this.apiCall({ destination: 'save_item_contact', data: this.newCustomer }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					this.selectedId = obj.contact_id
					this.saveLayawayCustomer()
				}
			}).catch(err => {
				console.log(err)
			})
		},
		saveLayawayCustomer() {
			// save the assigned contact as the customer for this layaway
			if (this.saving) { return }
			this.saving = true
			this.searchMessage = 'Saving layaway customer...'
			this.apiCall({ destination: 'save_item_layaway_contact', data: { order_id: this.orderId, contact_id: this.selectedId } }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {

					// any serialized items that might need updating in log books?
					let serialNumbers = []
					Object.values(this.orderDetail.items).forEach(item => {
						if (item.serial_number) {
							serialNumbers.push({ serial_number: item.serial_number, product_id: item.product_id })
						}
					})
					if (serialNumbers.length > 0) {
						this.apiCall({ destination: 'save_disposed_to_contact_info', data: { contact_id: this.selectedId, previous_contact_id: this.orderDetail.billing.contact_id, serial_numbers: serialNumbers } }).then(obj => {
							if (obj.status != 'success') {
								this.message = obj.message
							}
						}).catch(err => {
							console.log(err)
						})
					}

					let changeLog = []
					changeLog.push({ field: 'contact_id', old_value: this.orderDetail.billing.contact_id, new_value: this.selectedId })
					this.saveChangeLog(changeLog)
					this.editingSection = ''
					this.enableEdit = false
					this.searchMessage = ''
					this.selectedId = null
					this.editSearchTerm = ''
					this.newCustomer = {}
					this.getItem(this.orderId)
				}
			}).catch(err => {
				console.log(err)
			})

		},
		saveShipping() {
			// save changes to shipping address and pickup/ship to
			if (this.saving) { return }
			if (this.pickup != 1) {
				// not pickup, so we need to make sure required shipping fields are filled out before we save
				if (!this.editingItem.shipping_charge || !this.editingItem.address_1 || !this.editingItem.city || !this.editingItem.state || !this.editingItem.zip_code) {
					this.message = 'Please fill out all required shipping fields or select pickup.'
					this.messageStatus = 'error'
					return
				}
			}
			this.saving = true
			this.message = 'Saving shipping information...'
			this.messageStatus = 'waiting'
			this.apiCall({ destination: 'save_item_order_shipping', data: { order_id: this.orderId, shipping: this.editingItem, pickup: this.pickup } }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					let changeLog = []
					for (let key in this.editingItem) {
						if (this.editingItem[key] !== this.orderDetail.shipping[key]) {
							changeLog.push({ field: key, old_value: this.orderDetail.shipping[key], new_value: this.editingItem[key] })
						}
					}
					if (this.pickup !== this.orderDetail.shipping.pickup) {
						changeLog.push({ field: 'pickup', old_value: this.orderDetail.shipping.pickup, new_value: this.pickup })
					}
					this.saveChangeLog(changeLog)
					this.editingSection = ''
					this.enableEdit = false
					this.message = 'Shipping information saved!'
					this.messageTimeout = 2000
					this.getItem(this.orderId)
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})

		},
		removePayment() {
			// Remove an order payment from a layaway.
			if (!this.selectedId || this.saving) { return }

			this.saving = true
			this.message = 'Removing payment...'
			this.messageStatus = 'waiting'
			this.apiCall({ destination: 'remove_item_order_payment', data: { order_id: this.orderId, order_payment_id: this.selectedId } }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status

				if (obj.status === 'success') { 

					let changeLog = []
					if(this.paymentToRemove){
						changeLog.push({field: 'payment_removed', old_value: this.paymentToRemove.amount + ' on ' + this.paymentToRemove.payment_date, new_value: 'REMOVED'})
					}

					this.saveChangeLog(changeLog)

					//Reset modals
					this.confirmMessage = ''
					this.selectedId = null
					this.editingSection = ''
					this.enableEdit = false
					this.paymentToRemove = null

					this.message = 'Payment removed successfully.'
					this.messageTimeout = 200

					this.getItem(this.orderId)
				} else {
					this.saving = false 
					this.message = obj.message || 'An error occurred while removing the payment.'
				}
			}).catch(err => {
				this.saving = false
				this.paymentToRemove = null
				this.message = obj.message || 'An error occurred while removing the payment.' 
			})
		},
		returnToCustomer() {
			const route = this.returnRoute || sessionStorage.getItem('returnRoute')

			if (!route) {
				this.$router.push(this.ordersRoute)
				return 
			} 

			sessionStorage.removeItem('returnRoute')
			this.$router.push(route)
		},
		saveOrderItem() {
			// save changes to an item on this layaway
			if (this.saving) { return }
			this.saving = true
			this.message = 'Saving item...'
			this.messageStatus = 'waiting'
			this.apiCall({ destination: 'save_item_order_item', data: { order_id: this.orderId, item: this.editingItem } }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					let changeLog = []
					// console.log(this.selectedId, this.orderDetail.items[this.selectedId])
					for (let key in this.editingItem) {
						if (key.indexOf('previous_') === -1 && this.editingItem[key] !== this.orderDetail.items[this.selectedId][key]) {
							changeLog.push({ field: key, old_value: this.orderDetail.items[this.selectedId][key], new_value: this.editingItem[key] })
						}
					}
					this.saveChangeLog(changeLog)
					this.editingSection = ''
					this.enableEdit = false
					this.searchMessage = ''
					this.selectedId = null
					this.message = 'Item saved!'
					this.messageTimeout = 2000
					this.getItem(this.orderId)
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		addOrderItem() {
			if (this.saving) { return }
			this.saving = true
			this.message = 'Adding item...'
			this.messageStatus = 'waiting'
			this.apiCall({ destination: 'add_item_order_item', data: { order_id: this.orderId, item: this.editingItem } }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					let changeLog = []
					changeLog.push({ field: 'item_added', old_value: '', new_value: this.selectedId })
					this.saveChangeLog(changeLog)
					this.editingSection = ''
					this.enableEdit = false
					this.searchMessage = ''
					this.selectedId = null
					this.message = 'Item saved!'
					this.messageTimeout = 2000
					this.getItem(this.orderId)
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		removeOrderItem() {
			if (this.saving) { return }
			this.saving = true
			this.message = 'Removing item...'
			this.messageStatus = 'waiting'
			this.apiCall({ destination: 'remove_item_order_item', data: { order_id: this.orderId, item: this.editingItem } }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					let changeLog = []
					changeLog.push({ field: 'item_removed', old_value: this.selectedId, new_value: 'REMOVED' })
					this.saveChangeLog(changeLog)
					this.editingSection = ''
					this.enableEdit = false
					this.searchMessage = ''
					this.selectedId = null
					this.message = 'Item removed!'
					this.messageTimeout = 2000
					this.getItem(this.orderId)
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		saveExtraCharges() {
			if (this.saving) { return }
			let extraChargesKeys = ['shipping_charge', 'handling_charge', 'tax_charge']
			let valuesChanged = false
			for (let key of extraChargesKeys) {
				if (this.editingItem[key] !== this.orderDetail.totals[key]) {
					valuesChanged = true
					break
				}
			}
			if (!valuesChanged) {
				return
			}
			this.saving = true
			this.message = 'Saving extra charges...'
			this.messageStatus = 'waiting'
			this.apiCall({ destination: 'save_item_order_extra_charges', data: { order_id: this.orderId, extra_charges: this.editingItem } }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					let changeLog = []
					for (let key of extraChargesKeys) {
						if (this.editingItem[key] !== this.orderDetail.totals[key]) {
							changeLog.push({ field: key, old_value: this.orderDetail.totals[key], new_value: this.editingItem[key] })
						}
					}
					this.saveChangeLog(changeLog)
					this.editingSection = ''
					this.enableEdit = false
					this.searchMessage = ''
					this.selectedId = null
					this.message = 'Extra charges saved!'
					this.messageTimeout = 2000
					this.getItem(this.orderId)
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		addOrderFee() {
			this.editingItem.fees.push({ description: '', amount: 0 })
		},
		removeOrderFee(index) {
			this.editingItem.fees.splice(index, 1)
		},
		removeOrderItemFee(index) {
			this.editingItem.item_fees.splice(index, 1)
		},
		addOrderDiscount() {
			this.editingItem.push({ reason_code_id: '', description: '', amount: '' })
		},
		removeOrderDiscount(index) {
			this.editingItem.splice(index, 1)
		},
		saveFees() {
			if (this.saving) { return }
			this.saving = true
			this.message = 'Saving fees...'
			this.messageStatus = 'waiting'
			this.apiCall({ destination: 'save_item_order_fees', data: { order_id: this.orderId, fees: this.editingItem.fees, item_fees: this.editingItem.item_fees } }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					let changeLog = []
					for (let fee of this.editingItem.fees) {
						if (fee.order_fee_id) {
							// find the matching fee in this.orderDetail.fees and compare values to see if anything changed
							this.orderDetail.fees.forEach(originalFee => {
								if (originalFee.order_fee_id == fee.order_fee_id) {
									if (originalFee.description !== fee.description) {
										changeLog.push({ field: 'fee_description_' + fee.order_fee_id, old_value: originalFee.description, new_value: fee.description })
									}
									if (originalFee.amount !== fee.amount) {
										changeLog.push({ field: 'fee_amount_' + fee.order_fee_id, old_value: originalFee.amount, new_value: fee.amount })
									}
								}
							})
						}
					}
					for (let fee of this.editingItem.item_fees) {
						if (fee.order_item_fee_id) {
							// find the matching fee in this.orderDetail.item_fees and compare values to see if anything changed
							this.orderDetail.item_fees.forEach(originalFee => {
								if (originalFee.order_item_fee_id == fee.order_item_fee_id) {
									if (originalFee.notes !== fee.notes) {
										changeLog.push({ field: 'item_fee_notes_' + fee.order_item_fee_id, old_value: originalFee.notes, new_value: fee.notes })
									}
									if (originalFee.amount !== fee.amount) {
										changeLog.push({ field: 'item_fee_amount_' + fee.order_item_fee_id, old_value: originalFee.amount, new_value: fee.amount })
									}
								}
							})
						}
					}

					this.saveChangeLog(changeLog)
					this.editingSection = ''
					this.enableEdit = false
					this.searchMessage = ''
					this.selectedId = null
					this.message = 'Fees saved!'
					this.messageTimeout = 2000
					this.getItem(this.orderId)
				} else {
					this.message = obj.message || 'An error occurred while saving fees.'
				}
			}).catch(err => {
				console.log(err)
			})
		},
		saveDiscounts() {
			if (this.saving) { return }
			this.saving = true
			this.message = 'Saving discounts...'
			this.messageStatus = 'waiting'
			this.apiCall({ destination: 'save_item_order_discounts', data: { order_id: this.orderId, discounts: this.editingItem } }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					let changeLog = []
					for (let discount of this.editingItem) {
						if (discount.reason_code_id) {
							// find the matching discount in this.orderDetail.discounts and compare values to see if anything changed
							this.orderDetail.discounts.forEach(originalDiscount => {
								if (originalDiscount.reason_code_id == discount.reason_code_id) {
									if (originalDiscount.description !== discount.description) {
										changeLog.push({ field: 'discount_description_' + discount.reason_code_id, old_value: originalDiscount.description, new_value: discount.description })
									}
									if (originalDiscount.amount !== discount.amount) {
										changeLog.push({ field: 'discount_amount_' + discount.reason_code_id, old_value: originalDiscount.amount, new_value: discount.amount })
									}
								}
							})
						}
					}

					this.saveChangeLog(changeLog)
					this.editingSection = ''
					this.enableEdit = false
					this.searchMessage = ''
					this.selectedId = null
					this.message = 'Discounts saved!'
					this.messageTimeout = 2000
					this.getItem(this.orderId)
				} else {
					this.message = obj.message || 'An error occurred while saving discounts.'
				}
			}).catch(err => {
				console.log(err)
			})
		},
		cancelLayaway() {
			// cancel this layaway
			if (this.saving) { return }
			this.confirmCancelLayaway = false
			this.saving = true
			this.message = 'Canceling layaway...'
			this.messageStatus = 'waiting'
			this.apiCall({ destination: 'save_item_cancel_layaway', data: { order_id: this.orderId } }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status || 'error'
				if (obj.status == 'success') {
					this.saveChangeLog([{ field: 'status', old_value: this.orderDetail.order.order_status_code, new_value: 'CANCELED' }])
					this.backToList()
				} else {
					this.message = obj.message || 'An error occurred while canceling this layaway.'
				}
			}).catch(err => {
				console.log(err)
			})

		},
		getChangeLog() {
			this.changeLog = []
			this.searchMessage = 'Loading change log...'
			this.showChangeLog = true
			// get the change log for this layaway and display it in the change log modal
			this.apiCall({ destination: 'get_list_order_change_log', data: { order_id: this.orderId } }).then(obj => {
				if (obj.status == 'success') {
					this.changeLog = obj.change_log
					this.showChangeLog = true
					this.searchMessage = ''
				}
			}).catch(err => {
				console.log(err)
			})
		},
		saveChangeLog(changeLog) {
			if (changeLog.length == 0) {
				return
			}
			// save the change log for this layaway
			this.apiCall({ destination: 'save_item_order_change_log', data: { order_id: this.orderId, change_log: changeLog } }).then(obj => {
				if (obj.status == 'success') {
					// DEV NOTE: we can display the change log in the layaway change log modal
				}
			}).catch(err => {
				console.log(err)
			})
		},
		cancelEditModal() {
			if (this.editingSection == 'select_customer' || this.editingSection == 'create_customer') {
				this.editingSection = 'customer'
				this.hideSaveButton = false
				this.editingItem = JSON.parse(JSON.stringify(this.orderDetail.billing))
				this.editingItem.custom_fields = JSON.parse(JSON.stringify(this.orderDetail.custom_fields))
			} else if (this.editingSection == 'remove_item' || this.editingSection == 'add_item' || this.editingSection == 'edit_item') {
				this.editingSection = 'items'
				this.hideSaveButton = true
			} else {
				this.editingSection = ''
				this.searchMessage = ''
				this.selectedId = null
				this.editSearchTerm = ''
				this.showCreateCustomerOption = false
			}
		},
		printOrder(which) {
			this.orderDetail.exclude_cost = which == "customer";
			this.showPrint = true;
			setTimeout(() => {
				window.print();
				this.showPrint = false;
			}, 500);
		},
		printPackingSlip() {
			this.showPackingSlip = true;
			// slight delay while the screen renders the packing slip!
			setTimeout(() => {
				window.print();
				this.showPackingSlip = false;
			}, 500);
		},
		sendOrderEmails() {
			if (this.loading || !this.orderDetail.billing.email_address) {
				return;
			}
			this.message = 'Sending confirmation email...'
			this.messageStatus = 'waiting';
			this.loading = true
			this.apiCall({
				destination: "send_confirmation_email",
				data: { order_id: this.orderDetail.order.order_id },
			})
			.then((obj) => {
				this.messageStatus = obj.status;
				this.message = obj.message;
				this.loading = false;
				this.messageTimeout = 2000;
			})
			.catch((err) => {
				console.log(err);
			});
		},
		updateStatus() {
			// update this order status with this.orderStatus
		},
		backToList() {
			this.orderId = null
			this.orderDetail = {}
			this.enableEdit = false
			this.editingSection = ''
			this.getList()
		},
		doExport() {
			if (this.gettingExport) {
				return false
			}
			this.gettingExport = true
			this.exportMessage = 'Generating CSV for ' + this.paging.total_items + ' items...'
			this.filters.order_id = ''
			this.filters.do_export = true
			this.getList(1)
		},
		isCancelDisabled() {
			return this.orderDetail.order.order_status_code == 'COMPLETE' || this.orderDetail.order.order_status_code == 'CANCELED' || this.orderDetail.totals.payment_total > 0
		},
		isProductInLayaway(productId) {
			return this.getLayawayProductIds.includes(String(productId))
		}
	}
}
</script>

<style lang="scss" scoped>
.edit-link {
	font-size: 1.1em;
	padding: 0.25em 0.25em;
	cursor: pointer;
	color: #333333;
	&:hover {
		background: #e4e4e4;
	}
}
</style>
